import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CURRENCY } from '../../../../../../../../constants/currency';
import { getAsset } from '../../../../../../../../util/assets';
import CardElementPhotoSelectionControl from '../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import Button from '../../../../../../../../components/global/Button';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../../../../../constants/pro-custom-prints';

const propTypes = {
  className: PropTypes.string,
  albumMembershipPhoto: PropTypes.shape({
    photo: PropTypes.shape({
      id: PropTypes.string,
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  purchasedCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    album_custom_print: PropTypes.shape({
      price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }),
      discount_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }),
      custom_print: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
      }),
    }),
  }).isRequired,
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onPurchasedCustomPrintToggle: PropTypes.func.isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
  onConfigurationComplete: PropTypes.func,
};

const CustomPrintOption = ({
  className,
  albumMembershipPhoto,
  purchasedCustomPrint,
  cartItem,
  onPurchasedCustomPrintToggle,
  albumMembershipGroup,
  isAllPhotos,
  onConfigurationComplete,
}) => {
  const classes = classNames({
    [className]: !!className,
    'purchases-pending-configuration-option-item': true,
    'purchases-pending-configuration-option-item-album-custom-print': true,
    'is-selected': !!cartItem,
  });
  const {
    identity,
    album_custom_print: albumCustomPrint,
  } = purchasedCustomPrint;

  const { custom_print: customPrint = {} } = albumCustomPrint ?? {};
  const { name, description, preview_image: previewImage } = customPrint;
  const [configuringPhotoData, setConfiguringPhotoData] = useState(
    albumMembershipPhoto ?? null,
  );
  const { photo } = configuringPhotoData ?? {};
  const [selectingPhoto, setSelectingPhoto] = useState(false);
  // Account Card Related
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  // Specialty Print Related
  const [selectingTeamPhoto, setSelectingTeamPhoto] = useState(false);
  const includesTeamPhoto = albumCustomPrint?.photos_no > 1;

  const actionLabel = useMemo(() => {
    if (customPrint.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT) {
      if (cartItem) {
        return 'Added to cart';
      }

      if (includesTeamPhoto) {
        return 'Select photo & add a team photo';
      }

      return 'Select photo';
    }

    if (cartItem) {
      return 'Update configurations';
    }

    return 'Configure now';
  }, [customPrint, cartItem]);

  const onSelectPurchasedCustomPrintRequest = () => {
    if (customPrint.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT) {
      if (photo) {
        if (includesTeamPhoto && !cartItem) {
          setSelectingTeamPhoto(true);
          return;
        }

        onPurchasedCustomPrintToggle({
          photos: [{ photo, position: 0 }],
        });
        if (!cartItem) {
          onConfigurationComplete?.();
        }
        return;
      }

      setSelectingPhoto(true);
      return;
    }

    setConfigurationModalOpen(true);
  };

  return (
    <div className={classes}>
      <div className="purchases-pending-configuration-option-item-thumb">
        <img
          width={75}
          src={previewImage?.url ?? getAsset('/plaque.svg')}
          alt=""
        />
      </div>

      <div className="purchases-pending-configuration-option-item-content-wrap">
        <span className="purchases-pending-configuration-option-item-title">
          {name}
        </span>

        <div className="purchasing-option-item-content mb-2">
          <p className="text-sm lh-normal text-dark text-bold">
            {description ?? ''}
            {includesTeamPhoto &&
            customPrint.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT ? (
              <>Pick your favorite team photo to build your product!</>
            ) : (
              ''
            )}
          </p>
        </div>

        <Button
          pill
          btnSize="sm"
          btnOutline={!cartItem}
          className="text-bold"
          onClick={onSelectPurchasedCustomPrintRequest}
        >
          {actionLabel}
        </Button>
      </div>

      <CardElementPhotoSelectionControl
        forceOpen={selectingPhoto}
        onClose={() => setSelectingPhoto(false)}
        onRecordChange={matchedPhoto => {
          let photoData;
          // eslint-disable-next-line no-underscore-dangle
          if (matchedPhoto?.__typename === 'Photo') {
            photoData = {
              photo: matchedPhoto,
              album:
                matchedPhoto.albums?.records?.[0] ??
                albumMembershipGroup?.album,
            };
          } else {
            photoData = matchedPhoto;
          }
          setConfiguringPhotoData(photoData);
          if (includesTeamPhoto) {
            setSelectingTeamPhoto(true);
          } else {
            onPurchasedCustomPrintToggle({
              photos: [{ photo, position: 0 }],
            });
            onConfigurationComplete?.();
          }
        }}
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
        }}
        mappings={
          isAllPhotos
            ? undefined
            : [
                {
                  id: 'allPhotos',
                  label: 'All',
                  path: 'all_photos',
                },
                {
                  id: 'matches',
                  label: 'Matches',
                  path: 'matched_photos_extended',
                },
              ]
        }
        uploadDisabled
      />
      <AccountCardAlbumCustomPrintConfiguratorModal
        forceOpen={configurationModalOpen}
        albumCustomPrint={albumCustomPrint}
        onClose={() => setConfigurationModalOpen(false)}
        onFinished={({ accountCard }) => {
          onPurchasedCustomPrintToggle({
            accountCard,
          });
          onConfigurationComplete?.();
          setConfigurationModalOpen(false);
        }}
        deleteAccountCardOnDismiss
        identity={identity}
        albumMembershipGroup={albumMembershipGroup}
        isAllPhotos={isAllPhotos}
      />

      <CardElementPhotoSelectionControl
        uploadDisabled
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
        }}
        forceOpen={selectingTeamPhoto}
        onClose={() => {
          setSelectingTeamPhoto(false);
        }}
        onRecordChange={matchedPhoto => {
          // eslint-disable-next-line no-underscore-dangle
          if (matchedPhoto?.__typename === 'Photo') {
            onPurchasedCustomPrintToggle({
              photos: [
                { photo, position: 0 },
                { photo: matchedPhoto, position: 1 },
              ],
            });
          } else {
            onPurchasedCustomPrintToggle({
              photos: [
                { photo, position: 0 },
                { photo: matchedPhoto.photo, position: 1 },
              ],
            });
          }
          onConfigurationComplete?.();
          setSelectingTeamPhoto(false);
        }}
      />
    </div>
  );
};

CustomPrintOption.propTypes = propTypes;

export default CustomPrintOption;
